import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  salesCoachingScoringSettingsApi,
  ScoringRubric,
  UpdateRubricRequest,
  CreateRubricRequest,
  Employee,
} from "../api/salesCoachingScoringSettingsApi";

interface SalesCoachingScoringSettingsState {
  rubrics: ScoringRubric[];
  roleRubrics: ScoringRubric[];
  employees: Employee[];
  selectedRubric: ScoringRubric | null;
  settings: ScoringRubric | null; // Legacy support
  loading: boolean;
  saving: boolean;
  error: string | null;
  successMessage: string | null;
}

const initialState: SalesCoachingScoringSettingsState = {
  rubrics: [],
  roleRubrics: [],
  employees: [],
  selectedRubric: null,
  settings: null, // Legacy support
  loading: false,
  saving: false,
  error: null,
  successMessage: null,
};

// Rubric operations
export const fetchAllRubrics = createAsyncThunk(
  "salesCoachingScoringSettings/fetchAllRubrics",
  async () => {
    const response = await salesCoachingScoringSettingsApi.fetchAllRubrics();
    return response;
  },
);

export const fetchRoleRubrics = createAsyncThunk(
  "salesCoachingScoringSettings/fetchRoleRubrics",
  async () => {
    const response = await salesCoachingScoringSettingsApi.fetchRoleRubrics();
    return response;
  },
);

export const createRubric = createAsyncThunk(
  "salesCoachingScoringSettings/createRubric",
  async (rubric: CreateRubricRequest) => {
    const response = await salesCoachingScoringSettingsApi.createRubric(rubric);
    return response;
  },
);

export const updateRubric = createAsyncThunk(
  "salesCoachingScoringSettings/updateRubric",
  async ({ id, rubric }: { id: string; rubric: UpdateRubricRequest }) => {
    const response = await salesCoachingScoringSettingsApi.updateRubric(
      id,
      rubric,
    );
    return response;
  },
);

export const deleteRubric = createAsyncThunk(
  "salesCoachingScoringSettings/deleteRubric",
  async (id: string) => {
    await salesCoachingScoringSettingsApi.deleteRubric(id);
    return id;
  },
);

// Employee operations
export const fetchEmployees = createAsyncThunk(
  "salesCoachingScoringSettings/fetchEmployees",
  async () => {
    const response = await salesCoachingScoringSettingsApi.fetchEmployees();
    return response;
  },
);

export const updateEmployeeRole = createAsyncThunk(
  "salesCoachingScoringSettings/updateEmployeeRole",
  async ({ employeeId, role }: { employeeId: string; role: string }) => {
    const response = await salesCoachingScoringSettingsApi.updateEmployeeRole(
      employeeId,
      role,
    );
    return response;
  },
);

// Legacy support
export const fetchSettings = createAsyncThunk(
  "salesCoachingScoringSettings/fetchSettings",
  async () => {
    const response = await salesCoachingScoringSettingsApi.fetchSettings();
    return response;
  },
);

export const updateSettings = createAsyncThunk(
  "salesCoachingScoringSettings/updateSettings",
  async (settings: UpdateRubricRequest) => {
    const response =
      await salesCoachingScoringSettingsApi.updateSettings(settings);
    return response;
  },
);

const salesCoachingScoringSettingsSlice = createSlice({
  name: "salesCoachingScoringSettings",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    selectRubric: (
      state,
      action: PayloadAction<ScoringRubric | Partial<ScoringRubric>>,
    ) => {
      state.selectedRubric = action.payload as ScoringRubric;
    },
    clearSelectedRubric: (state) => {
      state.selectedRubric = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch settings
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch scoring settings";
      })
      // Update settings
      .addCase(updateSettings.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.saving = false;
        state.settings = action.payload;
        state.successMessage = "Scoring settings updated successfully";
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.saving = false;
        state.error =
          action.error.message || "Failed to update scoring settings";
      })
      // Fetch all rubrics
      .addCase(fetchAllRubrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllRubrics.fulfilled, (state, action) => {
        state.loading = false;
        state.rubrics = action.payload;
      })
      .addCase(fetchAllRubrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch rubrics";
      })
      // Fetch role rubrics
      .addCase(fetchRoleRubrics.fulfilled, (state, action) => {
        state.roleRubrics = action.payload;
      })
      // Create rubric
      .addCase(createRubric.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createRubric.fulfilled, (state, action) => {
        state.saving = false;
        state.rubrics.push(action.payload);
        state.successMessage = "Rubric created successfully";
      })
      .addCase(createRubric.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || "Failed to create rubric";
      })
      // Update rubric
      .addCase(updateRubric.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateRubric.fulfilled, (state, action) => {
        state.saving = false;
        const index = state.rubrics.findIndex(
          (r) => r.id === action.payload.id,
        );
        if (index !== -1) {
          state.rubrics[index] = action.payload;
        }
        if (state.selectedRubric?.id === action.payload.id) {
          state.selectedRubric = action.payload;
        }
        state.successMessage = "Rubric updated successfully";
      })
      .addCase(updateRubric.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || "Failed to update rubric";
      })
      // Delete rubric
      .addCase(deleteRubric.fulfilled, (state, action) => {
        state.rubrics = state.rubrics.filter((r) => r.id !== action.payload);
        if (state.selectedRubric?.id === action.payload) {
          state.selectedRubric = null;
        }
        state.successMessage = "Rubric deleted successfully";
      })
      // Fetch employees
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.employees = action.payload;
      })
      // Update employee role
      .addCase(updateEmployeeRole.fulfilled, (state, action) => {
        const index = state.employees.findIndex(
          (e) => e.id === action.payload.id,
        );
        if (index !== -1) {
          state.employees[index] = action.payload;
        }
        state.successMessage = "Employee role updated successfully";
      });
  },
});

export const {
  clearError,
  clearSuccessMessage,
  selectRubric,
  clearSelectedRubric,
} = salesCoachingScoringSettingsSlice.actions;
export default salesCoachingScoringSettingsSlice.reducer;
